import { useCallback, useRef } from 'react';
import { useUnmount } from 'react-use';

import { aiAgentsApi } from 'apis/aiAgents';

export interface UseRunAiAgentResult {
  /** Starts a run for the given agent, replacing any previous one. */
  runAgent: () => void;
  text: string | undefined;
  isLoading: boolean;
  isSuccess: boolean;
  isError: boolean;
  /** The run's exit reason when it ended in a failed status. */
  failureReason: string | undefined;
}

/**
 * A hook for drafting a reply by running an AI agent against a conversation.
 * The returned state describes the run of the given agent only.
 */
export function useRunAiAgent(
  agentId: string,
  dixaConversationId: string,
  userId: string,
): UseRunAiAgentResult {
  const [trigger, result] = aiAgentsApi.useLazyRunStreamAiAgentQuery();
  const triggerRef = useRef<{ abort: () => void } | null>(null);

  const runAgent = useCallback(() => {
    triggerRef.current?.abort();
    // `false` bypasses the cache so identical arguments run again.
    triggerRef.current = trigger(
      { agentId, dixaConversationId, userId },
      false,
    );
  }, [trigger, agentId, dixaConversationId, userId]);

  useUnmount(() => {
    triggerRef.current?.abort();
  });

  // Scoped to the current arguments, unlike `data`.
  const data = result.currentData;
  const isForAgent = result.originalArgs?.agentId === agentId;

  // Withheld while fetching, when it can still be an earlier run's draft.
  const isCurrent = isForAgent && !result.isFetching;
  const failureReason = isCurrent ? data?.failureReason : undefined;

  return {
    runAgent,
    text: isCurrent ? data?.text || undefined : undefined,
    isLoading: isForAgent
      ? result.isFetching || !!data?.meta.isLoading || !!data?.meta.isStreaming
      : false,
    isSuccess: isForAgent && !!data?.meta.isSuccess && !failureReason,
    // A recorded failure reason counts as an error.
    isError: (isForAgent && !!data?.meta.isError) || !!failureReason,
    failureReason,
  };
}
